'use client';

import { forwardRef } from 'react';
import { AnPicker } from 'react-anpicker/dist/cjs/components';
import { X } from 'react-feather';
import { Controller } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, Personalcard } from 'iconsax-react';

import { useMobile } from '@shared/hooks';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  Input,
  Label,
  LoadingSpinner,
} from '@shared/ui';

import 'react-anpicker/dist/styles.css';

import { language } from '../model/language';
import { useUpdateProfile } from '../model/useUpdateProfile';

type TUpdateGoldInvestmentProfileProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const UpdateGoldInvestmentProfile = ({
  open,
  onClose,
  onSuccess,
}: TUpdateGoldInvestmentProfileProps) => {
  const {
    control,
    handleSubmit,
    onSubmit,
    errors,
    isPending,
    errorMessage,
    clearErrorMessage,
    showSlowResponseWarning,
  } = useUpdateProfile({
    open,
    onSuccess,
  });

  const isMobile = useMobile();

  const FormContent = () => (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-2 md:p-6 mt-5 md:mt-0'>
      <div className='flex flex-col gap-2' id='datepicker-wrapper'>
        <Label className='body-14-regular leading-[150%]'>{language.birthDate}</Label>
        <Controller
          name='birthDate'
          control={control}
          render={({ field }) => (
            <AnPicker
              popupTargetId='datepicker-wrapper'
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                if (errorMessage) clearErrorMessage();
              }}
              // @ts-ignore
              inputControl={forwardRef<HTMLInputElement, any>((props, ref) => (
                <Input
                  readOnly
                  placeholder='1366/06/06'
                  className='min-w-[200px] h-[45px] !text-right p-4 cursor-pointer'
                  iconClassName='left-[8px]'
                  prefixinput={<Calendar className='ml-[10px]' size={16} />}
                  {...props}
                  ref={ref}
                />
              ))}
            />
          )}
        />
        <p className='text-red-1 body-12-regular mt-1 min-h-[18px]' dir='rtl'>
          {errors.birthDate?.message || errorMessage || '\u00A0'}
        </p>
      </div>

      <ul
        className={`space-y-3 mb-5 p-3 pr-6 bg-alertInfo body-14-regular rounded-[6px] list-disc ${
          showSlowResponseWarning ? 'mb-[71px]' : 'mb-[143px]'
        }`}
      >
        <li>{language.birthDateHint}</li>
        <li>{language.minAgeHint}</li>
      </ul>
      <AnimatePresence>
        {showSlowResponseWarning && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0, 0, 0.58, 1] }}
            className='mb-2 p-3 bg-orange/[8%] body-14-medium rounded-[6px] text-right leading-[28px]'
          >
            {language.slowResponseWarning}
          </motion.p>
        )}
      </AnimatePresence>

      <Button
        type='submit'
        disabled={isPending}
        className={`body-14-bold h-[60px] mb-2 mt-0 gap-2 ${
          isPending ? 'disabled:!bg-blue-1/10 disabled:!text-blue-1' : ''
        }`}
        id='submit_date_birth'
      >
        {isPending && <LoadingSpinner className='size-[16px]' />}
        {isPending ? language.submitPending : language.submit}
      </Button>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(v) => !v && onClose()}>
        <DrawerContent className='h-fit w-full'>
          <DrawerHeader>
            <div className='flex items-center justify-between'>
              <DrawerTitle>{language.title}</DrawerTitle>
              <DrawerClose>
                <X size={24} strokeWidth={1} />
              </DrawerClose>
            </div>
          </DrawerHeader>
          <FormContent />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-[430px] w-full'>
        <DialogHeader>
          <DialogTitle className='flex items-center justify-center gap-1 text-[14px] font-medium leading-[150%]'>
            <Personalcard size={22} />
            {language.title}
          </DialogTitle>
          <DialogClose>
            <X size={24} strokeWidth={1} />
          </DialogClose>
        </DialogHeader>
        <FormContent />
      </DialogContent>
    </Dialog>
  );
};
